import { JSX, SVGProps } from 'react'

import { Document } from 'src/components/icon'
import {
  FiletypeDicom,
  FiletypeDoc,
  FiletypeJpg,
  FiletypePdf,
  FiletypePng,
  FiletypePpt,
  FiletypeXls,
  FiletypeZip,
} from 'src/components/svg'
import {
  AcceptableFileType,
  getFileTypeLabel,
} from 'src/constants/acceptableFileTypes'

import { binaryValidateFileItem, MediaType } from './binaryValidation'
import { assertNever } from '../../../utils/assertNever'

export type NewFileItem = {
  isNew: true
  isValid: boolean
  fileName: string
  fileUrl: string
  fileSize: number
  file: File
  mediaType?: MediaType
}
export type SavedFileItem = {
  uid: string
  isNew: false
  isValid: true
  fileName: string
  fileUrl: string
  fileSize: number
  fileExtension: string
  savedAt: string
}

export type FileItem = NewFileItem | SavedFileItem

const MAX_FILE_COUNT = 30
const MAX_ONE_FILE_SIZE = 200 * 1024 * 1024 // 200MB
const MAX_ZIP_FILE_SIZE = 500 * 1024 * 1024 // 500MB
const MAX_AMOUNT_FILE_SIZE = 500 * 1024 * 1024 // 500MB

export type FileType =
  'PNG' | 'JPG' | 'PDF' | 'DICOM' | 'DOC' | 'XLS' | 'PPT' | 'ZIP' | 'Other'

export const validateFileItems = async (
  files: FileItem[],
  acceptType: AcceptableFileType[],
): Promise<{ isValid: boolean; errorMessage?: string }> => {
  const invalidBinaryFiles = await Promise.all(
    files.map(async file => {
      if (isNewFileItem(file)) {
        const result = await binaryValidateFileItem({
          file: file.file,
          acceptType,
        })
        return !result.isValid
      }
      return false
    }),
  )
  if (invalidBinaryFiles.some(result => result)) {
    const acceptTypeLabels = Array.from(
      new Set(acceptType.map(type => getFileTypeLabel(type))),
    )
    return {
      isValid: false,
      errorMessage: `${acceptTypeLabels.join('・')}形式以外のファイルはアップロードできません。`,
    }
  }
  if (files.some(file => !checkFileNameCharacters(file.fileName))) {
    return {
      isValid: false,
      errorMessage: 'ファイル名に¥/:*?"<>|の文字は使用できません。',
    }
  }
  if (files.some(file => !checkFileNameLength(file.fileName))) {
    return {
      isValid: false,
      errorMessage: 'ファイル名は拡張子を除き100文字以内にしてください。',
    }
  }
  if (files.some(file => !checkFileSize(file))) {
    return {
      isValid: false,
      errorMessage:
        'アップロードの上限サイズ(200MB)を超えているファイルがあります。',
    }
  }
  if (files.length > MAX_FILE_COUNT) {
    return {
      isValid: false,
      errorMessage: 'アップロード可能なファイル数（30個）を超えています。',
    }
  }
  const totalSize = files.reduce((acc, file) => acc + file.fileSize, 0)
  if (totalSize > MAX_AMOUNT_FILE_SIZE) {
    return {
      isValid: false,
      errorMessage:
        'ファイルの合計サイズは500MB以下でアップロードしてください。',
    }
  }

  return {
    isValid: true,
  }
}

export const checkFileSize = (file: FileItem): boolean => {
  const isZip = file.fileName.endsWith('.zip')
  const maxSize = isZip ? MAX_ZIP_FILE_SIZE : MAX_ONE_FILE_SIZE
  return file.fileSize <= maxSize
}

export const checkFileNameCharacters = (fileName: string): boolean => {
  // ¥, :, \, /, *, ?, ", <, >, | はファイル名に使えない
  return !/[¥\\/:*?"<>|]/.test(fileName)
}

export const checkFileNameLength = (fileName: string): boolean => {
  return fileName.split('.').slice(0, -1).join('.').length <= 100
}

export const getExtension = (fileName: string): string => {
  if (!fileName.includes('.')) {
    return ''
  }
  return fileName.split('.').pop() ?? ''
}

export const getFileType = (extension: string): FileType => {
  if (!extension) return 'Other'
  const extensionLowerCase = extension.toLowerCase()
  switch (extensionLowerCase) {
    case 'jpeg':
    case 'jpg':
      return 'JPG'
    case 'png':
      return 'PNG'
    case 'pdf':
      return 'PDF'
    case 'dcm':
      return 'DICOM'
    case 'doc':
    case 'docx':
      return 'DOC'
    case 'xls':
    case 'xlsx':
      return 'XLS'
    case 'ppt':
    case 'pptx':
      return 'PPT'
    case 'zip':
      return 'ZIP'
    default:
      return 'Other'
  }
}

export const getFileTypeIcon = (
  fileType: FileType,
  props?: {
    size?: 'sm' | 'md' | 'lg' | (string & {})
  } & Omit<SVGProps<SVGSVGElement>, 'width' | 'height'>,
): JSX.Element => {
  switch (fileType) {
    case 'PNG':
      return <FiletypePng {...props} />
    case 'JPG':
      return <FiletypeJpg {...props} />
    case 'PDF':
      return <FiletypePdf {...props} />
    case 'DICOM':
      return <FiletypeDicom {...props} />
    case 'DOC':
      return <FiletypeDoc {...props} />
    case 'XLS':
      return <FiletypeXls {...props} />
    case 'PPT':
      return <FiletypePpt {...props} />
    case 'ZIP':
      return <FiletypeZip {...props} />
    case 'Other':
      return <Document {...props} />
    default:
      return assertNever(fileType)
  }
}

export const isPreviewable = (fileType: FileType): boolean => {
  switch (fileType) {
    case 'PNG':
    case 'JPG':
    case 'PDF':
      return true
    case 'DICOM':
    case 'DOC':
    case 'XLS':
    case 'PPT':
    case 'ZIP':
    case 'Other':
      return false
    default:
      return assertNever(fileType)
  }
}

export const canFullscreen = (fileType: FileType): boolean => {
  switch (fileType) {
    case 'PNG':
    case 'JPG':
    case 'PDF':
    case 'DICOM':
      return true
    case 'DOC':
    case 'XLS':
    case 'PPT':
    case 'Other':
    case 'ZIP':
      return false
    default:
      return assertNever(fileType)
  }
}

export const isNewFileItem = (item: FileItem): item is NewFileItem => item.isNew
export const isSavedFileItem = (item: FileItem): item is SavedFileItem =>
  !item.isNew

export const truncateFileName = (fileName: string): string => {
  const fileNameWithoutExtension = fileName.split('.').slice(0, -1).join('.')
  if (fileNameWithoutExtension.length <= 22) {
    return fileName
  } else {
    const extension = fileName.split('.').pop()
    const abbreviatedFileName =
      fileNameWithoutExtension.substring(0, 20) +
      '...' +
      fileNameWithoutExtension.slice(-2) +
      '.' +
      extension
    return abbreviatedFileName
  }
}

export const fileSizeWithUnit = (fileSize: number): string => {
  // 0.1KB未満の場合は0.1KBと表示する
  if (fileSize < 100) {
    return '0.1KB'
  } else if (fileSize < 1024 * 1024) {
    // 有効桁数を1桁にする
    return `${(Math.round((fileSize / 1024) * 10) / 10).toFixed(1)}KB`
  } else {
    return `${(Math.round((fileSize / 1024 ** 2) * 10) / 10).toFixed(1)}MB`
  }
}

/** 例えばimage.pngが複数アップロードされた場合、二つ目のファイルをimage(1).pngにrenameする */
export const renameFileNameToUnique = (
  targetFileName: string,
  existingFilenames: string[],
): string => {
  // 同一のファイル名が存在しない場合はそのまま返す
  if (existingFilenames.every(fileName => fileName !== targetFileName)) {
    return targetFileName
  }
  // 拡張子がない場合はそのまま返す
  if (!targetFileName.includes('.')) {
    return targetFileName
  }

  // 例: image.png -> image, image(2).png -> image （拡張子直前のカッコは取り除く）
  const extractBaseName = (fileName: string) => {
    return fileName
      .replace(/\(\d+\)\./, '.')
      .split('.')
      .slice(0, -1)
      .join('.')
  }

  const targetBaseName = extractBaseName(targetFileName)
  const targetExtension = targetFileName.split('.').pop()

  // 採番済みのファイル名から番号を抽出し、昇順に並べる
  const existingNumbers = existingFilenames
    .filter(fileName => extractBaseName(fileName) === targetBaseName)
    .map(fileName => {
      const match = fileName.match(/\((\d+)\)\./)
      return match ? parseInt(match[1]) : 0
    })
    .sort((a, b) => a - b)

  // 欠番があればそのうち最小の番号、なければ続く数を返す 例) [1, 2, 4] -> 3, [1, 2, 3] -> 4
  const nextCount = existingNumbers.reduce((acc, num) => {
    return acc === num ? acc + 1 : acc
  }, 1)

  return `${targetBaseName}(${nextCount}).${targetExtension}`
}
